import { Box, Button } from "@mui/material";
import React, { useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import StyledImageCroper from "./styledImageCroper";
import rollbar from "../../../rollbarConfig";

const ImageCropper = ({
  src,
  aspectRatio = 1,
  fixedCropWidth = "85",
  onCancel,
  onCropComplete,
  outputFormat = "image/jpeg", // "image/jpeg" | "image/png"
  quality = 1.0, // JPEG quality (0–1)
}) => {
  const [crop, setCrop] = useState();
  const imageRef = useRef(null);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;

    const crop = makeAspectCrop(
      {
        unit: "px",
        width: parseFloat(fixedCropWidth),
      },
      aspectRatio,
      width,
      height
    );

    if (!crop || isNaN(crop.width) || isNaN(crop.height)) {
      return;
    }

    const centeredCrop = centerCrop(crop, width, height);

    if (!centeredCrop || isNaN(centeredCrop.width) || isNaN(centeredCrop.height)) {
      return;
    }

    setCrop(centeredCrop);
  };

  const handleCrop = () => {
    if (!imageRef.current || !crop?.width || !crop?.height) {
      console.error("Crop data is invalid");
      return;
    }

    const image = imageRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // keep original resolution
    const canvas = document.createElement("canvas");
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          const ext = outputFormat === "image/png" ? "png" : "jpg";
          const file = new File([blob], `cropped-image.${ext}`, {
            type: outputFormat,
          });

          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            resolve({ base64: reader.result, file });
          };
          reader.onerror = reject;
        },
        outputFormat,
        outputFormat === "image/jpeg" ? quality : undefined // only JPEG uses quality
      );
    });
  };

  return (
    <StyledImageCroper>
      <Box className="cropper-wrap">
        <ReactCrop
          crop={crop}
          keepSelection
          aspect={aspectRatio}
          minWidth={fixedCropWidth}
          onChange={(pixelCrop) => setCrop(pixelCrop)}
        >
          <img
            ref={imageRef}
            src={src}
            alt="image-cropped"
            style={{ minHeight: "50vh", maxHeight: "50vh" }}
            onLoad={onImageLoad}
          />
        </ReactCrop>
        <Box className="cropper-btn">
          <Button
            className="crop-btn"
            onClick={async () => {
              try {
                const { base64, file } = await handleCrop();
                onCropComplete({ base64, file });
              } catch (error) {
                rollbar.error("Image cropping failed", error);
                console.error("Cropping failed:", error);
              }
            }}
          >
            Crop
          </Button>
          <Button className="cancel-btn" onClick={onCancel}>
            Cancel
          </Button>
        </Box>
      </Box>
    </StyledImageCroper>
  );
};

export default ImageCropper;
